import React from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { AnalyticsFiltersState, AnalyticsInterval, AnalyticsMetric, AnalyticsWorkspaceScope } from "../types";
import { Spinner } from "../../../components/ui/spinner";

interface AnalyticsFiltersBarProps {
  filters: AnalyticsFiltersState;
  isLoading?: boolean;
  onChange: (next: AnalyticsFiltersState) => void;
  onRefresh: () => void;
}

const AnalyticsFiltersBar = ({ filters, isLoading = false, onChange, onRefresh }: AnalyticsFiltersBarProps) => {
  const patch = <K extends keyof AnalyticsFiltersState>(key: K, value: AnalyticsFiltersState[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="flex flex-wrap items-end gap-2 rounded border p-3">
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">Workspace scope</p>
        <Select
          value={filters.workspaceScope}
          onValueChange={(value: AnalyticsWorkspaceScope) => patch("workspaceScope", value)}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">Current workspace</SelectItem>
            <SelectItem value="all">All workspaces</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">From</p>
        <Input type="date" value={filters.from} onChange={(e) => patch("from", e.target.value)} />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">To</p>
        <Input type="date" value={filters.to} onChange={(e) => patch("to", e.target.value)} />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">Interval</p>
        <Select
          value={filters.interval}
          onValueChange={(value: AnalyticsInterval) => patch("interval", value)}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hour">hour</SelectItem>
            <SelectItem value="day">day</SelectItem>
            <SelectItem value="week">week</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">Metric</p>
        <Select value={filters.metric} onValueChange={(value: AnalyticsMetric) => patch("metric", value)}>
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="events">events</SelectItem>
            <SelectItem value="save">save</SelectItem>
            <SelectItem value="publish">publish</SelectItem>
            <SelectItem value="failures">failures</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" onClick={onRefresh} disabled={isLoading}>
        {isLoading ? <Spinner className="mr-1 h-4 w-4" /> : null}
        Refresh
      </Button>
    </div>
  );
};

export default AnalyticsFiltersBar;
